import React, { Fragment, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, BrowserRouter } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import lazyRetry from '../utils/routes';
import ROUTES from '../constants/system/routes';
import GuestGuard from '../utils/guards/GuestGuard';
import ErrorBoundary from '../views/system/ErrorBoundary';
import AuthGuard from '../utils/guards/AuthGuard';
import MainLayout from '../layouts/mainLayout/MainLayout';
import { ThemeProvider } from '../context/themeContext';
import SplashScreen from '../components/SplashScreen';
import GlobalNotificationSetup from '../components/GlobalNotificationSetup';

const basicRoutesConfig = [
  {
    path: ROUTES.SYSTEM['400'].PATH,
    component: lazy(() =>
      lazyRetry(() => import('../views/system/Error400'), '400'),
    ),
  },
  {
    path: ROUTES.SYSTEM['401'].PATH,
    component: lazy(() =>
      lazyRetry(() => import('../views/system/Error401'), '401'),
    ),
  },
  {
    path: ROUTES.SYSTEM['404'].PATH,
    component: lazy(() =>
      lazyRetry(() => import('../views/system/Error404'), '404'),
    ),
  },
  {
    path: ROUTES.SYSTEM['500'].PATH,
    component: lazy(() =>
      lazyRetry(() => import('../views/system/Error500'), '500'),
    ),
  },
  {
    path: ROUTES.SYSTEM['502'].PATH,
    component: lazy(() =>
      lazyRetry(() => import('../views/system/Error502'), '502'),
    ),
  },
  {
    guard: GuestGuard,
    path: ROUTES.AUTH.LOGIN.PATH,
    component: lazy(() =>
      lazyRetry(() => import('../views/auth/login/index'), 'login'),
    ),
  },
];

const defaultRoutesConfig = () => [
  ...basicRoutesConfig,
  {
    path: ROUTES.ROOT.INDEX.PATH,
    component: () => <Navigate to={ROUTES.DASHBOARD.INDEX.PATH} />,
  },
  {
    path: '*',
    guard: AuthGuard,
    layout: MainLayout,
    routes: [
      {
        path: ROUTES.ROOT.INDEX.PATH,
        component: () => <Navigate to={ROUTES.DASHBOARD.INDEX.PATH} />,
      },
      {
        path: ROUTES.DASHBOARD.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/dashboard/main'), 'dashboard'),
        ),
      },
      {
        path: ROUTES.USERS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/users/main'), 'users'),
        ),
      },
      {
        path: ROUTES.USERS.EDIT.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/users/edit'), 'usersEdit'),
        ),
      },
      {
        path: ROUTES.TRANSACTIONS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/transactions/main'), 'transactions'),
        ),
      },
      {
        path: ROUTES.TRANSACTIONS.REPORT.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/transactions/report'),
            'transactionsReport',
          ),
        ),
      },
      {
        path: ROUTES.TRANSACTIONS.SETTINGS.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/transactions/settings'),
            'transactionsSettings',
          ),
        ),
      },
      {
        path: ROUTES.TRANSACTIONS.SPECIAL_OFFERS.INDEX.PATH,
        component: lazy(() => lazyRetry(() => import('../views/specialOffers/main'), 'specialOffers')),
      },
      {
        path: ROUTES.PROMO_CODES.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/promoCodes/main'), 'promoCodes'),
        ),
      },
      {
        path: ROUTES.PROMO_CODES.EDIT.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/promoCodes/edit'), 'promoCodesEdit'),
        ),
      },
      {
        path: ROUTES.REFERRALS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/referrals/main'), 'referrals'),
        ),
      },
      {
        path: ROUTES.REFERRALS.ADD_CONVERSION.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/referrals/add'), 'referralsAdd'),
        ),
      },
      {
        path: ROUTES.DOMAINS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/domains/main'), 'domains'),
        ),
      },
      {
        path: ROUTES.NETWORK_PRESETS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/networkPresets/main'),
            'networkPresets',
          ),
        ),
      },
      {
        path: ROUTES.NETWORK_PRESETS.EDIT.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/networkPresets/edit'),
            'networkPresetsEdit',
          ),
        ),
      },
      {
        path: ROUTES.SOURCE_PRESETS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/sourcePresets/main'),
            'sourcePresets',
          ),
        ),
      },
      {
        path: ROUTES.SOURCE_PRESETS.EDIT.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/sourcePresets/edit'),
            'sourcePresetsEdit',
          ),
        ),
      },
      {
        path: ROUTES.PRESET_CATEGORIES.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/presetCategories/main'),
            'presetCategories',
          ),
        ),
      },
      {
        path: ROUTES.NEWS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/news/main'), 'news'),
        ),
      },
      {
        path: ROUTES.NEWS.EDIT.PATH,
        component: lazy(() =>
          lazyRetry(() => import('../views/news/edit'), 'newsEdit'),
        ),
      },
      {
        path: ROUTES.TRAFFIC_FILTERS.INDEX.PATH,
        component: lazy(() =>
          lazyRetry(
            () => import('../views/trafficFilters/main'),
            'trafficFilters',
          ),
        ),
      },
      {
        component: () => <Navigate to={ROUTES.SYSTEM['404'].PATH} />,
      },
    ],
  },
];

const renderRoutes = (routes) =>
  routes ? (
    <Suspense fallback={<SplashScreen />}>
      <Routes>
        {routes.map((route) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const Component = route.component;
          return (
            <Route
              key={uuidv4()}
              path={route.path}
              element={
                <Guard>
                  <Layout>
                    {route.routes ? renderRoutes(route.routes) : <Component />}
                  </Layout>
                </Guard>
              }
            />
          );
        })}
      </Routes>
    </Suspense>
  ) : null;

function RoutesIndex() {


  return (
    <ThemeProvider>
      <CssBaseline />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ErrorBoundary>
            <SnackbarProvider maxSnack={3}>
              <GlobalNotificationSetup>
                {renderRoutes(defaultRoutesConfig())}
              </GlobalNotificationSetup>
            </SnackbarProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default RoutesIndex;
